import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { yupResolver } from '@hookform/resolvers/yup';
import { boolean, mixed, object, string } from 'yup';
import { useForm } from 'react-hook-form';
import { Iconify } from '@/components';
import {
  FormProvider,
  RHFSelect,
  RHFSwitch,
  RHFTextField,
} from '@/components/hook-form';
import {
  ScheduledJob,
  ScheduledJobExecutionType,
  ScheduledJobPayload,
} from '../types/scheduled-jobs';
import {
  buildCronPreview,
  formatCronPreviewDate,
} from '../utils/cron-preview';

type ScheduledJobFormDrawerProps = {
  open: boolean;
  currentJob?: ScheduledJob | null;
  onClose: VoidFunction;
  onSubmitJob: (payload: ScheduledJobPayload) => Promise<void>;
};

type ScheduledJobFormValues = {
  name: string;
  description: string;
  enabled: boolean;
  cronExpression: string;
  timeZone: string;
  executionType: ScheduledJobExecutionType;
  httpMethod: string;
  targetUrl: string;
  headersJson: string;
  bodyJson: string;
  handlerKey: string;
  timeoutMs: string;
  lockKey: string;
};

const EXECUTION_TYPES: ScheduledJobExecutionType[] = ['HTTP', 'INTERNAL'];
const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const DEFAULT_VALUES: ScheduledJobFormValues = {
  name: '',
  description: '',
  enabled: true,
  cronExpression: '',
  timeZone: 'Europe/London',
  executionType: 'HTTP',
  httpMethod: 'POST',
  targetUrl: '',
  headersJson: '{}',
  bodyJson: '{}',
  handlerKey: '',
  timeoutMs: '10000',
  lockKey: '',
};

const isValidJsonString = (value?: string | null) => {
  if (!value?.trim()) return true;

  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
};

const formatJsonField = (value: unknown) => {
  if (value === undefined || value === null || value === '') return '{}';
  if (typeof value === 'string') return value;

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '{}';
  }
};

const parseJsonField = (value: string) => {
  if (!value.trim()) return {};
  return JSON.parse(value);
};

const buildDefaultValues = (
  currentJob?: ScheduledJob | null
): ScheduledJobFormValues => {
  if (!currentJob) return DEFAULT_VALUES;

  return {
    name: currentJob.name || '',
    description: currentJob.description || '',
    enabled: currentJob.enabled,
    cronExpression: currentJob.cronExpression || '',
    timeZone: currentJob.timeZone || 'Europe/London',
    executionType: currentJob.executionType || 'HTTP',
    httpMethod: currentJob.httpMethod || 'POST',
    targetUrl: currentJob.targetUrl || '',
    headersJson: formatJsonField(currentJob.headersJson),
    bodyJson: formatJsonField(currentJob.bodyJson),
    handlerKey: currentJob.handlerKey || '',
    timeoutMs:
      currentJob.timeoutMs !== undefined && currentJob.timeoutMs !== null
        ? String(currentJob.timeoutMs)
        : '10000',
    lockKey: currentJob.lockKey || '',
  };
};

const Schema = object().shape({
  name: string().required('Name is required'),
  description: string().required('Description is required'),
  enabled: boolean().required(),
  cronExpression: string().required('Cron expression is required'),
  timeZone: string().required('Timezone is required'),
  executionType: mixed<ScheduledJobExecutionType>()
    .oneOf(EXECUTION_TYPES)
    .required('Execution type is required'),
  httpMethod: string().when('executionType', {
    is: 'HTTP',
    then: string().required('HTTP method is required'),
  }),
  targetUrl: string().when('executionType', {
    is: 'HTTP',
    then: string().required('Target URL is required'),
  }),
  handlerKey: string().when('executionType', {
    is: 'INTERNAL',
    then: string().required('Handler key is required'),
  }),
  headersJson: string().test(
    'valid-json',
    'Headers JSON must be valid JSON',
    isValidJsonString
  ),
  bodyJson: string().test(
    'valid-json',
    'Body JSON must be valid JSON',
    isValidJsonString
  ),
  timeoutMs: string().test(
    'positive-number',
    'Timeout must be a positive number',
    (value) => {
      if (!value?.trim()) return true;
      const parsed = Number(value);
      return !Number.isNaN(parsed) && parsed > 0;
    }
  ),
});

const backendFieldMap: Record<string, keyof ScheduledJobFormValues> = {
  cron_expression: 'cronExpression',
  cronExpression: 'cronExpression',
  time_zone: 'timeZone',
  timeZone: 'timeZone',
  execution_type: 'executionType',
  executionType: 'executionType',
  http_method: 'httpMethod',
  httpMethod: 'httpMethod',
  target_url: 'targetUrl',
  targetUrl: 'targetUrl',
  headers_json: 'headersJson',
  headersJson: 'headersJson',
  body_json: 'bodyJson',
  bodyJson: 'bodyJson',
  handler_key: 'handlerKey',
  handlerKey: 'handlerKey',
  timeout_ms: 'timeoutMs',
  timeoutMs: 'timeoutMs',
  lock_key: 'lockKey',
  lockKey: 'lockKey',
  name: 'name',
  description: 'description',
  enabled: 'enabled',
};

const extractBackendMessage = (error: any) => {
  if (typeof error === 'string') return error;
  if (typeof error?.message === 'string') return error.message;
  if (typeof error?.error === 'string') return error.error;
  return 'Something went wrong';
};

const getBackendErrors = (error: any) => {
  if (error?.errors) return error.errors;
  if (error?.data?.errors) return error.data.errors;
  return null;
};

export default function ScheduledJobFormDrawer({
  open,
  currentJob,
  onClose,
  onSubmitJob,
}: ScheduledJobFormDrawerProps) {
  const [serverError, setServerError] = useState('');
  const defaultValues = useMemo(
    () => buildDefaultValues(currentJob),
    [currentJob]
  );

  const methods = useForm<ScheduledJobFormValues>({
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = methods;

  const executionType = watch('executionType');
  const cronExpression = watch('cronExpression');
  const timeZone = watch('timeZone');
  const isEdit = Boolean(currentJob);
  const cronPreview = useMemo(
    () => buildCronPreview(cronExpression, timeZone),
    [cronExpression, timeZone]
  );

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setServerError('');
    }
  }, [defaultValues, open, reset]);

  const applyBackendErrors = (error: any) => {
    const errors = getBackendErrors(error);
    let fieldErrorApplied = false;

    if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
      Object.entries(errors as Record<string, unknown>).forEach(
        ([field, value]) => {
          const mappedField = backendFieldMap[field];
          if (!mappedField) return;

          let message = JSON.stringify(value);
          if (Array.isArray(value)) {
            message = value.join('\n');
          } else if (typeof value === 'string') {
            message = value;
          }

          setError(mappedField, {
            type: 'server',
            message,
          });
          fieldErrorApplied = true;
        }
      );
    }

    setServerError(
      fieldErrorApplied
        ? 'Please review the highlighted fields.'
        : extractBackendMessage(error)
    );
  };

  const onSubmit = async (values: ScheduledJobFormValues) => {
    setServerError('');

    const timeoutMs = values.timeoutMs.trim()
      ? Number(values.timeoutMs)
      : undefined;

    const payload: ScheduledJobPayload = {
      name: values.name.trim(),
      description: values.description.trim(),
      enabled: values.enabled,
      cronExpression: values.cronExpression.trim(),
      timeZone: values.timeZone.trim(),
      executionType: values.executionType,
      ...(timeoutMs ? { timeoutMs } : {}),
      ...(values.lockKey.trim() ? { lockKey: values.lockKey.trim() } : {}),
    };

    if (values.executionType === 'HTTP') {
      payload.httpMethod = values.httpMethod;
      payload.targetUrl = values.targetUrl.trim();
      payload.headersJson = parseJsonField(values.headersJson);
      payload.bodyJson = parseJsonField(values.bodyJson);
    } else {
      payload.handlerKey = values.handlerKey.trim();
    }

    try {
      await onSubmitJob(payload);
    } catch (error) {
      applyBackendErrors(error);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: 1, sm: 560 }, maxWidth: '100%' },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="h6">
            {isEdit ? 'Edit scheduled job' : 'Create scheduled job'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEdit ? currentJob?.name : 'Configure a DB-driven cron job'}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <Iconify icon="eva:close-fill" />
        </IconButton>
      </Box>

      <Divider />

      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2.5} sx={{ p: 3 }}>
          {serverError && <Alert severity="error">{serverError}</Alert>}

          <RHFTextField name="name" label="Name" />
          <RHFTextField
            name="description"
            label="Description"
            multiline
            minRows={3}
          />
          <RHFSwitch name="enabled" label="Enabled" />
          <RHFTextField name="cronExpression" label="Cron expression" />
          {cronExpression?.trim() && (
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                border: '1px solid',
                borderColor: cronPreview.error ? 'error.light' : 'divider',
                borderRadius: 1,
              }}
            >
              <Typography
                variant="caption"
                color={cronPreview.error ? 'error' : 'text.secondary'}
              >
                {cronPreview.error ? 'Preview unavailable' : 'Next runs'}
              </Typography>

              {cronPreview.error ? (
                <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                  {cronPreview.error}
                </Typography>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                  {cronPreview.dates.map((date) => (
                    <Chip
                      key={date.toISOString()}
                      size="small"
                      variant="outlined"
                      label={formatCronPreviewDate(date, timeZone)}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          )}
          <RHFTextField name="timeZone" label="Timezone" />

          <RHFSelect name="executionType" label="Execution type">
            {EXECUTION_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </RHFSelect>

          {executionType === 'HTTP' ? (
            <>
              <RHFSelect name="httpMethod" label="HTTP method">
                {HTTP_METHODS.map((method) => (
                  <option key={method} value={method}>
                    {method}
                  </option>
                ))}
              </RHFSelect>
              <RHFTextField name="targetUrl" label="Target URL" />
              <RHFTextField
                name="headersJson"
                label="Headers JSON"
                multiline
                minRows={4}
              />
              <RHFTextField
                name="bodyJson"
                label="Body JSON"
                multiline
                minRows={5}
              />
            </>
          ) : (
            <RHFTextField name="handlerKey" label="Handler key" />
          )}

          <RHFTextField name="timeoutMs" label="Timeout ms" type="number" />
          <RHFTextField name="lockKey" label="Lock key" />

          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <LoadingButton color="inherit" variant="outlined" onClick={onClose}>
              Cancel
            </LoadingButton>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              {isEdit ? 'Save changes' : 'Create job'}
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>
    </Drawer>
  );
}
